import { TableOfContentsItem } from '@/components/template/template-table-of-contents';

import { slug } from '@/utils/slug-utils';

// Generic interface for components that can have table of contents
export interface ComponentWithTableOfContents {
  id: string;
  tableOfContents?: {
    enabled?: boolean | null;
    title?: string | null;
  } | null;
}

// Function type for building linked section IDs
export type LinkedSectionIdBuilder<T extends ComponentWithTableOfContents> = (
  component: T
) => string;

// Configuration for table of contents generation
export interface TableOfContentsConfig<T extends ComponentWithTableOfContents> {
  linkedSectionIdBuilder: LinkedSectionIdBuilder<T>;
}

/**
 * Generic table of contents transformer that works with any component type
 * that implements ComponentWithTableOfContents interface
 */
export function createTableOfContents<T extends ComponentWithTableOfContents>(
  components: T[],
  config: TableOfContentsConfig<T>
): TableOfContentsItem[] {
  const tocItems: TableOfContentsItem[] = [];

  components.forEach((component) => {
    // Check if component has table of contents enabled and has a title
    if (
      component.tableOfContents?.enabled &&
      component.tableOfContents?.title
    ) {
      const title = component.tableOfContents.title;
      const url = `#${config.linkedSectionIdBuilder(component)}`;

      tocItems.push({
        title,
        url,
      });
    }
  });

  return tocItems;
}

/**
 * Default linked section ID builder that matches the existing linkedSectionID logic
 * Uses slug of tableOfContents title if available, otherwise falls back to component ID
 */
export function defaultLinkedSectionIdBuilder<
  T extends ComponentWithTableOfContents,
>(component: T): string {
  const title = component.tableOfContents?.title;
  return title ? slug(title) : component.id;
}
